<template>
    <div class="robot-control">
        <!-- 页面头部 -->
        <div class="header">
            <el-button type="text" @click="goBack" class="back-btn">
                <i class="el-icon-arrow-left"></i> 返回
            </el-button>
            <h1>机器人控制</h1>
            <div class="header-right"></div>
        </div>

        <!-- 主要内容区域 -->
        <div v-if="robotLoading" class="loading-container">
            <el-row type="flex" justify="center" align="middle">
                <el-col>
                    <el-loading-spinner size="large"></el-loading-spinner>
                    <p class="loading-text">加载中...</p>
                </el-col>
            </el-row>
        </div>

        <div v-else-if="robot" class="main-content">

            <!-- 机器人控制面板 -->
            <div class="control-panel">
                <h3 @click="handleTitleClick" :style="{ cursor: isAdmin ? 'pointer' : 'default', userSelect: 'none' }">控制面板</h3>

                <!-- 单开型控制 -->
                <div v-if="robot.details.device_type === 'single' || robot.details.device_type === 'compatible'" class="single-control-section">
                    <!-- 单开控制按钮 -->
                    <el-button
                            :class="['single-toggle-btn', {
                                        'active': robot.details.status === 'single_on' && robot.details.online,
                                        'disabled': !robot.details.online
                                    }]"
                            size="large"
                            @click.stop="toggleSingleRobot"
                    >
                        <i v-if="robot.details.online" class="el-icon-switch-button"></i>
                        <span v-else class="single-btn-offline-text">已离线</span>
                    </el-button>
                </div>

                <!-- 双开型控制 -->
                <div v-else-if="robot.details.device_type === 'double'" class="double-control-section">
                    <!-- 双开型机器人控制 -->
                    <div class="double-controls-new">
                        <el-button
                                :class="['double-btn', 'left-btn', {
                                        'active': robot.details.status === 'double_left' && robot.details.online,
                                        'disabled': !robot.details.online
                                    }]"
                                size="large"
                                @click.stop="pressLeft"
                        >
                            <i class="el-icon-arrow-left"></i>
                        </el-button>
                        <el-button
                                :class="['double-btn', 'right-btn', {
                                        'active': robot.details.status === 'double_right' && robot.details.online,
                                        'disabled': !robot.details.online
                                    }]"
                                size="large"
                                @click.stop="pressRight"
                        >
                            <i class="el-icon-arrow-right"></i>
                        </el-button>
                    </div>
                </div>

                <!-- 离线提示 -->
                <div v-if="!robot.details.online" class="offline-notice">
                    <el-alert
                            title="机器人当前离线，无法进行控制操作"
                            type="warning"
                            show-icon
                            :closable="false"
                    >
                        <template slot="desc">
                            请检查机器人的网络连接或电源状态。
                        </template>
                    </el-alert>
                </div>
            </div>

            <!-- 管理员调试卡片 -->
            <el-card v-if="showAdminDebug && isAdmin" class="robot-info-card admin-debug-card">
                <div slot="header" class="card-header">
                    <h3>🔧 管理员调试</h3>
                    <el-button type="text" @click="showAdminDebug = false">
                        <i class="el-icon-close"></i>
                    </el-button>
                </div>

                <div class="debug-buttons">
                    <div class="debug-actions">
                      <el-button
                          type="danger"
                          icon="el-icon-refresh-left"
                          @click="handleRestoreFactory('pro')"
                      >
                        恢复出厂设置
                      </el-button>
                    </div>
                    <div class="debug-actions">
                      <el-button
                          type="success"
                          icon="el-icon-refresh-left"
                          @click="handleRestoreFactory('bemfa')"
                      >
                        恢复bfa出厂设置
                      </el-button>
                    </div>
                </div>
            </el-card>

            <!-- 机器人信息卡片 -->
            <el-card class="robot-info-card">
                <div slot="header" class="card-header">
                    <h3>基本信息</h3>
                    <el-button v-if="canEditSettings" type="primary" size="small" @click="openEditModal">
                        编辑
                    </el-button>
                </div>

                <div class="robot-details">
                    <div class="detail-row">
                        <!-- 左列 -->
                        <div class="detail-column">
                            <div class="detail-item">
                                <span class="detail-label">设备名称：</span>
                                <span class="detail-value detail-value-ellipsis" :title="robot.details.name">{{ robot.details.name }}</span>
                            </div>
                            <div class="detail-item" v-if="robot.details.device_type !== 'compatible'">
                                <span class="detail-label">控制模式：</span>
                                <el-tag :type="robot.details.mode === 'tap' ? 'primary' : 'warning'" size="small">
                                    {{ robot.details.mode === 'tap' ? '单击' : '切换' }}
                                </el-tag>
                                <el-tooltip content="单击模式：机器人在执行动作后会自动归位；切换模式：机器人在执行后需要再次按下才会归位" placement="top">
                                    <i class="el-icon-question help-icon"></i>
                                </el-tooltip>
                            </div>
                            <div class="detail-item" v-if="robot.details.device_type !== 'compatible'">
                                <span class="detail-label">开机自动按压：</span>
                                <el-tag :type="robot.details.boot_execute ? 'success' : 'info'" size="small">
                                    {{ robot.details.boot_execute ? '开启' : '关闭' }}
                                </el-tag>
                                <el-tooltip content="开启后，机器人每次开机完成初始化时会自动执行一次按压" placement="top">
                                    <i class="el-icon-question help-icon"></i>
                                </el-tooltip>
                            </div>
                            <div class="detail-item" v-if="robot.details.device_type !== 'compatible'">
                                <span class="detail-label">静默模式：</span>
                                <el-tag :type="robot.details.silence_mode ? 'success' : 'info'" size="small">
                                    {{ robot.details.silence_mode ? '开启' : '关闭' }}
                                </el-tag>
                                <el-tooltip content="静默模式开启之后，机器人执行操作时将不会亮灯" placement="top">
                                    <i class="el-icon-question help-icon"></i>
                                </el-tooltip>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">当前状态：</span>
                                <el-tag :type="robot.details.online ? 'success' : 'danger'" size="small">
                                    {{ robot.details.online ? '在线' : '离线' }}
                                </el-tag>
                            </div>
                        </div>

                        <!-- 右列 -->
                        <div class="detail-column">
                            <div class="detail-item" v-if="robot.details.device_type !== 'compatible'">
                                <span class="detail-label">复位延迟：</span>
                                <span class="detail-value">{{ robot.details.delay }}ms</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">巴法云连接：</span>
                                <el-tag :type="robot.details.enable_bemfa ? 'success' : 'info'" size="small">
                                    {{ robot.details.enable_bemfa ? '启用' : '禁用' }}
                                </el-tag>
                                <el-tooltip content="如果不需要接入米家，关闭后可增加机器人稳定性和启动速度" placement="top">
                                    <i class="el-icon-question help-icon"></i>
                                </el-tooltip>
                            </div>
                        </div>
                    </div>

                    <!-- 分享信息 -->
                    <template v-if="robot.share_info && robot.share_info.is_shared">
                        <div class="share-info-section">
                            <div class="detail-item">
                                <span class="detail-label">分享来源：</span>
                                <span class="detail-value">{{ robot.share_info.from_user }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">分享权限：</span>
                                <el-tag 
                                    :type="robot.share_info.role === 'admin' ? 'primary' : robot.share_info.role === 'operator' ? 'success' : 'warning'" 
                                    size="small"
                                >
                                    {{ 
                                        robot.share_info.role === 'admin' ? '管理员' : 
                                        robot.share_info.role === 'operator' ? '操作员' : '查看者' 
                                    }}
                                </el-tag>
                            </div>
                        </div>
                    </template>
                </div>
            </el-card>

            <!-- 角度设置卡片 -->
            <el-card class="robot-info-card" v-show="robot.details.device_type !== 'compatible'">
                <div slot="header" class="card-header">
                    <h3>角度设置</h3>
                    <el-button v-if="canEditSettings" type="primary" size="small" @click="openAngleModal">
                        编辑
                    </el-button>
                </div>

                <div class="robot-details">
                    <!-- 单开型机器人角度 -->
                    <template v-if="robot.details.device_type === 'single'">
                        <div class="detail-item">
                            <span class="detail-label">按压角度：</span>
                            <span class="detail-value">{{ angleSettings.single_angle }}°</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">默认角度：</span>
                            <span class="detail-value">{{ angleSettings.off_angle }}°</span>
                        </div>
                    </template>
                    <!-- 双开型机器人角度 -->
                    <template v-else-if="robot.details.device_type === 'double'">
                        <div class="detail-item">
                            <span class="detail-label">左转角度：</span>
                            <span class="detail-value">{{ angleSettings.double_left_angle }}°</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">右转角度：</span>
                            <span class="detail-value">{{ angleSettings.double_right_angle }}°</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">默认角度：</span>
                            <span class="detail-value">{{ angleSettings.off_angle }}°</span>
                        </div>
                    </template>
                </div>
            </el-card>

            <!-- 分享设置 -->
            <el-card class="robot-info-card share-settings-section">
                <div slot="header" class="card-header">
                    <h3>分享设置</h3>
                    <el-button v-if="!robot.share_info || !robot.share_info.is_shared" type="primary" size="small" @click="goToSharePage">
                        管理分享
                    </el-button>
                    <el-button v-else type="warning" size="small" @click="cancelShare">
                        取消分享
                    </el-button>
                </div>

                <div class="share-hint">
                    <i class="el-icon-share"></i>
                    <span v-if="!robot.share_info || !robot.share_info.is_shared">您可以将机器人分享给其他用户</span>
                    <span v-else>此设备分享自：{{ robot.share_info.from_user }}</span>
                </div>
            </el-card>

            <!-- 定时任务管理 -->
            <el-card class="robot-info-card schedule-tasks-section">
                <div slot="header" class="card-header">
                    <h3>定时任务</h3>
                    <el-button 
                        v-if="canEditSettings"
                        type="primary" 
                        size="small" 
                        @click="openCreateTaskModal"
                    >
                        创建任务
                    </el-button>
                </div>

                <!-- 加载中 -->
                <div v-if="tasksLoading" v-loading="true" class="tasks-loading" style="min-height: 100px;">
                </div>

                <!-- 任务列表 -->
                <div v-else-if="scheduledTasks.length > 0" class="tasks-list">
                    <el-card 
                        v-for="task in scheduledTasks" 
                        :key="task.ID" 
                        :class="['task-item', { 'task-item-disabled': !canEditSettings }]"
                        @click.native="canEditSettings && openEditTaskModal(task)"
                        shadow="hover"
                    >
                        <div class="task-header">
                            <div class="task-name">{{ task.name }}</div>
                            <el-tag :type="task.enabled ? 'success' : 'info'" size="mini">
                                {{ task.enabled ? '启用' : '禁用' }}
                            </el-tag>
                        </div>

                        <div class="task-details">
                            <div class="task-detail-row">
                                <span class="task-label">类型：</span>
                                <span class="task-value">{{ getTaskTypeLabel(task.type) }}</span>
                            </div>
                            <div class="task-detail-row">
                                <span class="task-label">动作：</span>
                                <span class="task-value">{{ getActionLabel(task.action) }}</span>
                            </div>
                            <div class="task-detail-row">
                                <span class="task-label">执行时间：</span>
                                <span class="task-value">{{ formatTaskDescription(task) }}</span>
                            </div>
                        </div>

                        <div v-if="canEditSettings" class="task-actions">
                            <el-button 
                                type="text"
                                @click.stop="toggleTaskEnabled(task)"
                            >
                                <i :class="task.enabled ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
                                {{ task.enabled ? '禁用' : '启用' }}
                            </el-button>
                            <el-button 
                                type="text"
                                class="delete-btn"
                                @click.stop="deleteTask(task)"
                            >
                                <i class="el-icon-delete"></i>
                                删除
                            </el-button>
                        </div>
                    </el-card>
                </div>

                <!-- 空状态 -->
                <el-empty v-else description="暂无定时任务">
                    <template v-if="canEditSettings" slot="description">
                        <span>点击右上角按钮创建定时任务</span>
                    </template>
                </el-empty>
            </el-card>

            <!-- 操作日志 -->
            <div class="operation-history">
                <h3>操作历史</h3>
                <div v-loading="logsLoading" class="logs-container">
                    <el-timeline :reverse="false" v-if="operationRecords.length > 0">
                        <el-timeline-item
                                v-for="(record, index) in operationRecords"
                                :key="record.id || index"
                                :timestamp="formatTime(record.time)"
                        >
                            <el-card>
                                <div class="record-header">
                                    <div class="record-info">
                                        <i :class="getActionIcon(record.action)"></i>
                                        <span class="record-text">{{ record.content }}</span>
                                        <el-tag 
                                            v-if="record.result" 
                                            :type="record.result === 'success' ? 'success' : 'danger'"
                                            size="mini"
                                            class="result-tag"
                                        >
                                            {{ record.result === 'success' ? '成功' : '失败' }}
                                        </el-tag>
                                    </div>
                                    <div class="operator-info">
                                        <el-tag 
                                            :type="record.isScheduledTask ? 'primary' : ''"
                                            size="mini"
                                        >
                                            {{ record.operatorName }}
                                        </el-tag>
                                    </div>
                                </div>
                                <div v-if="record.errorMessage" class="error-message">
                                    <el-alert
                                        :title="record.errorMessage"
                                        type="error"
                                        :closable="false"
                                        show-icon
                                        size="small"
                                    />
                                </div>
                            </el-card>
                        </el-timeline-item>
                    </el-timeline>
                    <el-empty v-else-if="!logsLoading" description="暂无操作记录"></el-empty>
                    
                    <!-- 加载更多状态 -->
                    <div v-if="loadingMore" class="loading-more">
                        <el-loading-spinner size="small"></el-loading-spinner>
                        <span>加载更多...</span>
                    </div>
                    
                    <!-- 没有更多数据提示 -->
                    <div v-else-if="!hasMoreLogs && operationRecords.length > 0" class="no-more-data">
                        <span>没有更多记录了</span>
                    </div>
                    
                </div>
            </div>
        </div>

        <div v-else class="error-container">
            <el-empty description="无法加载机器人信息"></el-empty>
            <el-button type="primary" @click="fetchRobot" class="retry-btn">重试</el-button>
        </div>

        <!-- 编辑机器人对话框 -->
        <el-dialog
            title="编辑机器人信息"
            :visible.sync="editDialogVisible"
            width="600px"
            :close-on-click-modal="false"
        >
            <el-form :model="editForm" :rules="editFormRules" ref="editForm" label-width="120px">
                <el-form-item label="设备名称" prop="name">
                    <el-input v-model="editForm.name" placeholder="请输入设备名称" maxlength="30"></el-input>
                </el-form-item>

                <el-form-item label="设备状态" prop="status">
                    <el-select v-model="editForm.status" placeholder="请选择设备状态" style="width: 100%">
                        <el-option
                            v-for="item in statusOptions"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        ></el-option>
                    </el-select>
                </el-form-item>

                <template v-if="robot && robot.details.device_type !== 'compatible'">
                    <el-form-item label="控制模式" prop="mode">
                        <el-select v-model="editForm.mode" placeholder="请选择控制模式" style="width: 100%">
                            <el-option label="单击模式" value="tap"></el-option>
                            <el-option label="切换模式" value="toggle"></el-option>
                        </el-select>
                        <div class="form-tip">单击模式：机器人在执行动作后会自动归位；切换模式：机器人在执行后需要再次按下才会归位</div>
                    </el-form-item>

                    <el-form-item label="开机自动按压" prop="boot_execute">
                        <el-switch v-model="editForm.boot_execute"></el-switch>
                        <div class="form-tip">开启后，机器人每次开机完成初始化时会自动执行一次按压</div>
                    </el-form-item>

                    <el-form-item label="静默模式" prop="silence_mode">
                        <el-switch v-model="editForm.silence_mode"></el-switch>
                        <div class="form-tip">静默模式开启之后，机器人执行操作时将不会亮灯</div>
                    </el-form-item>

                    <el-form-item label="复位延迟时间(毫秒)" prop="delay">
                        <el-input-number v-model="editForm.delay" :min="100" :max="3600000" :step="100" style="width: 100%"></el-input-number>
                    </el-form-item>
                </template>

                <el-form-item label="巴法云连接" prop="enable_bemfa">
                    <el-switch v-model="editForm.enable_bemfa"></el-switch>
                    <div class="form-tip">启用后将连接到巴法云平台</div>
                </el-form-item>
            </el-form>

            <div slot="footer" class="dialog-footer">
                <el-button @click="editDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="saveEdit" :loading="editLoading">保存</el-button>
            </div>
        </el-dialog>

        <!-- 编辑角度设置对话框 -->
        <el-dialog
            :visible.sync="angleDialogVisible"
            width="600px"
            :close-on-click-modal="false"
            @closed="angleGuideDialogVisible = false"
        >
            <div slot="title" class="angle-dialog-title">
                <span>编辑角度设置</span>
                <el-button
                    type="primary"
                    size="mini"
                    plain
                    icon="el-icon-question"
                    @click="angleGuideDialogVisible = true"
                >
                    操作指引
                </el-button>
            </div>

            <el-form :model="angleForm" :rules="angleFormRules" ref="angleForm" label-width="120px">
                <!-- 单开型机器人角度 -->
                <template v-if="robot && robot.details.device_type === 'single'">
                    <el-form-item label="按压角度" prop="single_angle">
                        <el-input-number v-model="angleForm.single_angle" :min="0" :max="180" style="width: 150px"></el-input-number>
                        <span style="margin: 0 10px">°</span>
                        <el-slider v-model="angleForm.single_angle" :min="0" :max="180" style="flex: 1"></el-slider>
                    </el-form-item>
                    <el-form-item label="默认角度" prop="off_angle">
                        <el-input-number v-model="angleForm.off_angle" :min="0" :max="180" style="width: 150px"></el-input-number>
                        <span style="margin: 0 10px">°</span>
                        <el-slider v-model="angleForm.off_angle" :min="0" :max="180" style="flex: 1"></el-slider>
                    </el-form-item>
                </template>

                <!-- 双开型机器人角度 -->
                <template v-else-if="robot && robot.details.device_type === 'double'">
                    <el-form-item label="左转角度" prop="double_left_angle">
                        <el-input-number v-model="angleForm.double_left_angle" :min="0" :max="90" style="width: 150px"></el-input-number>
                        <span style="margin: 0 10px">°</span>
                        <el-slider v-model="angleForm.double_left_angle" :min="0" :max="90" style="flex: 1"></el-slider>
                    </el-form-item>
                    <el-form-item label="右转角度" prop="double_right_angle">
                        <el-input-number v-model="angleForm.double_right_angle" :min="0" :max="90" style="width: 150px"></el-input-number>
                        <span style="margin: 0 10px">°</span>
                        <el-slider v-model="angleForm.double_right_angle" :min="0" :max="90" style="flex: 1"></el-slider>
                    </el-form-item>
                    <el-form-item label="默认角度" prop="off_angle">
                        <el-input-number v-model="angleForm.off_angle" :min="0" :max="180" style="width: 150px"></el-input-number>
                        <span style="margin: 0 10px">°</span>
                        <el-slider v-model="angleForm.off_angle" :min="0" :max="180" style="flex: 1"></el-slider>
                    </el-form-item>
                </template>
            </el-form>

            <div slot="footer" class="dialog-footer">
                <el-button @click="resetAngleSettings">重置</el-button>
                <el-button @click="angleDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="saveAngleSettings" :loading="angleLoading">保存</el-button>
            </div>
        </el-dialog>

        <angle-guide-dialog :visible.sync="angleGuideDialogVisible"></angle-guide-dialog>

        <!-- 创建/编辑定时任务对话框 -->
        <el-dialog
            :title="isEditingTask ? '编辑定时任务' : '创建定时任务'"
            :visible.sync="scheduleDialogVisible"
            width="650px"
            :close-on-click-modal="false"
        >
            <el-form :model="scheduleForm" :rules="scheduleFormRules" ref="scheduleForm" label-width="120px">
                <el-form-item label="任务名称" prop="name">
                    <el-input v-model="scheduleForm.name" placeholder="请输入任务名称" maxlength="100"></el-input>
                </el-form-item>

                <el-form-item label="任务类型" prop="type">
                    <el-select v-model="scheduleForm.type" placeholder="请选择任务类型" style="width: 100%" @change="onTaskTypeChange">
                        <el-option
                            v-for="item in taskTypeOptions"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        ></el-option>
                    </el-select>
                </el-form-item>

                <el-form-item prop="action">
                    <template slot="label">
                        <span>执行动作</span>
                        <el-popover
                            v-if="robot && robot.details && robot.details.device_type === 'single'"
                            placement="top"
                            width="360"
                            trigger="click"
                            content="在控制模式设置为单击模式的时候，不同的执行动作仅影响日志记录还有机器人在页面显示的状态，实际上都会执行一次完整的按压动作"
                        >
                            <i
                                slot="reference"
                                class="el-icon-question help-icon schedule-action-help-icon"
                                @click.stop
                            ></i>
                        </el-popover>
                    </template>
                    <el-select v-model="scheduleForm.action" placeholder="请选择执行动作" style="width: 100%">
                        <el-option
                            v-for="item in actionOptions"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        ></el-option>
                    </el-select>
                </el-form-item>

                <!-- 固定时间任务 - 时间设置 -->
                <el-form-item v-if="isTimedScheduleType(scheduleForm.type)" label="执行时间" prop="schedule_time">
                    <el-time-picker
                        v-model="scheduleForm.schedule_time"
                        format="HH:mm:ss"
                        value-format="HH:mm:ss"
                        placeholder="选择时间"
                        style="width: 100%"
                    ></el-time-picker>
                </el-form-item>

                <!-- 自定义星期 - 星期选择 -->
                <el-form-item v-if="scheduleForm.type === 'weekly'" label="选择星期" prop="weekdays">
                    <el-checkbox-group v-model="scheduleForm.weekdays">
                        <el-checkbox
                            v-for="day in weekdayOptions"
                            :key="day.value"
                            :label="day.value"
                        >{{ day.label }}</el-checkbox>
                    </el-checkbox-group>
                </el-form-item>

                <!-- 循环执行 - 间隔时间 -->
                <el-form-item v-if="scheduleForm.type === 'interval'" label="间隔时间(秒)" prop="interval_seconds">
                    <el-input-number v-model="scheduleForm.interval_seconds" :min="5" :max="86400" style="width: 100%"></el-input-number>
                    <div class="form-tip">⚠️ 循环执行任务只能创建1个</div>
                </el-form-item>

                <!-- 任务数量提示 -->
                <el-alert
                    v-if="scheduleForm.type !== 'interval'"
                    :title="`固定时间任务最多创建${maxFixedTimeTasks}个`"
                    type="info"
                    :closable="false"
                    show-icon
                ></el-alert>
            </el-form>

            <div slot="footer" class="dialog-footer">
                <el-button @click="scheduleDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="saveScheduleTask" :loading="scheduleLoading">{{ isEditingTask ? '保存修改' : '创建任务' }}</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import {
  controlRobot, getRobot, updateRobot, updateRobotAngles, getDefaultAngleSettings, restoreRobot,
  restoreBemfaRobot
} from '@/api/robot'
import {getUserRobotLogs} from '@/api/log'
import {deleteRobotShare} from "@/api/share"
import {createScheduledTask, getScheduledTasks, updateScheduledTask, deleteScheduledTask, toggleScheduledTask} from '@/api/schedule'
import {Message, MessageBox} from 'element-ui'
import {mapGetters} from 'vuex'
import AngleGuideDialog from '@/components/AngleGuideDialog.vue'

export default {
    name: 'RobotControl',
    components: {
        AngleGuideDialog
    },
    data() {
        return {
            robot: null,
            robotLoading: true,
            // 管理员调试相关
            showAdminDebug: false,
            titleClickCount: 0,
            titleClickTimer: null,
            operationRecords: [],
            logsLoading: false,
            controlTimer: null,
            // 机器人按钮控制防抖
            isProcessing: false,
            // 分页相关
            currentPage: 1,
            pageSize: 10,
            hasMoreLogs: true,
            loadingMore: false,
            // 编辑相关
            editDialogVisible: false,
            editLoading: false,
            editForm: {
                name: '',
                status: '',
                mode: '',
                boot_execute: false,
                silence_mode: false,
                delay: 500,
                enable_bemfa: true,
            },
            editFormRules: {
                name: [
                    { required: true, message: '请输入设备名称', trigger: 'blur' },
                    { min: 1, max: 30, message: '长度在 1 到 30 个字符', trigger: 'blur' }
                ],
                status: [
                    { required: true, message: '请选择设备状态', trigger: 'change' }
                ],
                mode: [
                    { required: true, message: '请选择控制模式', trigger: 'change' }
                ],
                delay: [
                    { required: true, message: '请输入复位延迟时间', trigger: 'blur' },
                    { type: 'number', min: 100, max: 3600000, message: '复位延迟时间必须在100-3600000毫秒之间', trigger: 'blur' }
                ]
            },
            // 角度设置相关
            angleDialogVisible: false,
            angleGuideDialogVisible: false,
            angleLoading: false,
            angleSettings: {
                single_angle: 42,
                double_left_angle: 42,
                double_right_angle: 42,
                off_angle: 90,
            },
            angleForm: {
                single_angle: 42,
                double_left_angle: 42,
                double_right_angle: 42,
                off_angle: 90,
            },
            angleFormRules: {
                single_angle: [
                    { required: true, message: '请输入按压角度', trigger: 'blur' },
                    { type: 'number', min: 0, max: 180, message: '角度必须在0-180度之间', trigger: 'blur' }
                ],
                double_left_angle: [
                    { required: true, message: '请输入左转角度', trigger: 'blur' },
                    { type: 'number', min: 0, max: 90, message: '角度必须在0-90度之间', trigger: 'blur' }
                ],
                double_right_angle: [
                    { required: true, message: '请输入右转角度', trigger: 'blur' },
                    { type: 'number', min: 0, max: 90, message: '角度必须在0-90度之间', trigger: 'blur' }
                ],
                off_angle: [
                    { required: true, message: '请输入默认角度', trigger: 'blur' },
                    { type: 'number', min: 0, max: 180, message: '角度必须在0-180度之间', trigger: 'blur' }
                ]
            },
            // 定时任务相关
            scheduleDialogVisible: false,
            scheduleLoading: false,
            scheduledTasks: [],
            tasksLoading: false,
            isEditingTask: false,
            editingTaskId: null,
            maxFixedTimeTasks: 30,
            scheduleForm: {
                name: '',
                type: 'daily',
                action: '',
                schedule_time: '00:00:00',
                hour: 0,
                minute: 0,
                second: 0,
                weekdays: [],
                interval_seconds: 5,
            },
            scheduleFormRules: {
                name: [
                    { required: true, message: '请输入任务名称', trigger: 'blur' },
                    { min: 1, max: 100, message: '长度在 1 到 100 个字符', trigger: 'blur' }
                ],
                action: [
                    { required: true, message: '请选择执行动作', trigger: 'change' }
                ],
                schedule_time: [
                    { required: true, message: '请选择执行时间', trigger: 'change' }
                ],
                weekdays: [
                    { required: true, type: 'array', min: 1, message: '请至少选择一个星期', trigger: 'change' }
                ],
                interval_seconds: [
                    { required: true, message: '请输入间隔时间', trigger: 'blur' },
                    { type: 'number', min: 5, message: '间隔时间不能小于5秒', trigger: 'blur' }
                ]
            },
            taskTypeOptions: [
                { value: 'daily', label: '每天执行' },
                { value: 'weekly', label: '自定义星期' },
                { value: 'workday', label: '法定工作日' },
                { value: 'holiday', label: '法定节假日' },
                { value: 'interval', label: '循环执行' },
            ],
            weekdayOptions: [
                { value: 1, label: '周一' },
                { value: 2, label: '周二' },
                { value: 3, label: '周三' },
                { value: 4, label: '周四' },
                { value: 5, label: '周五' },
                { value: 6, label: '周六' },
                { value: 7, label: '周日' },
            ],
        }
    },
    computed: {
        ...mapGetters(['id']),
        // 是否是管理员（用户ID为1）
        isAdmin() {
            return this.id === 1
        },
        // 状态选项 - 根据设备类型动态生成
        statusOptions() {
            if (!this.robot || !this.robot.details) {
                return [];
            }
            
            if (this.robot.details.device_type === 'single' || this.robot.details.device_type === 'compatible') {
                return [
                    { value: 'single_on', label: '开' },
                    { value: 'off', label: '关' },
                ];
            } else if (this.robot.details.device_type === 'double') {
                return [
                    { value: 'double_left', label: '左开' },
                    { value: 'double_right', label: '右开' },
                    { value: 'off', label: '关' },
                ];
            }
            
            return [];
        },
        // 动作选项 - 根据设备类型动态生成
        actionOptions() {
            if (!this.robot || !this.robot.details) {
                return [];
            }
            
            if (this.robot.details.device_type === 'single' || this.robot.details.device_type === 'compatible') {
                return [
                    { value: 'single_switch', label: '切换' },
                    { value: 'single_on', label: '开启' },
                    { value: 'off', label: '关闭' },
                ];
            } else if (this.robot.details.device_type === 'double') {
                return [
                    { value: 'double_switch', label: '左右切换' },
                    { value: 'double_left', label: '左开' },
                    { value: 'double_right', label: '右开' },
                    { value: 'off', label: '关闭' },
                ];
            }
            
            return [];
        },
        // 检查是否可以编辑设置（根据分享权限）
        canEditSettings() {
            // 如果不是分享的设备，允许编辑
            if (!this.robot || !this.robot.share_info || !this.robot.share_info.is_shared) {
                return true;
            }
            // 如果是分享的设备且是管理员，允许编辑
            if (this.robot.share_info.role === 'admin') {
                return true;
            }
            // 其他情况（operator 或 viewer）不允许编辑
            return false;
        },
    },
    created() {
        this.fetchRobot()
        this.fetchOperationLogs()
        this.fetchScheduledTasks()
        // 设置定时器定期刷新机器人状态
        this.controlTimer = setInterval(() => {
            this.fetchRobot()
        }, 5000) // 每5秒刷新一次
    },
    mounted() {
        // 延迟添加滚动监听，确保DOM完全渲染
        setTimeout(() => {
            this.addScrollListener()
        }, 1000)
    },
    beforeDestroy() {
        // 清除定时器
        if (this.controlTimer) {
            clearInterval(this.controlTimer)
        }
        // 移除滚动监听
        this.removeScrollListener()
    },
    methods: {
        // 切换单开型机器人状态
        toggleSingleRobot() {
            if (!this.robot.details.online) {
                Message.closeAll()
                Message.warning("机器人不在线")
                return
            }

            if (this.isProcessing) return

            let newAction
            switch (this.robot.details.status) {
                case 'single_on':
                    newAction = 'off'
                    break
                case 'off':
                    newAction = 'single_on'
                    break
                default:
                    newAction = 'single_on'
            }

            this.handleControlRobot(this.robot.details.ID, newAction)
        },

        // 获取机器人信息
        fetchRobot() {
            const robotId = this.$route.params.id
            getRobot(robotId).then(res => {
                this.robot = res.data
                // 解析角度设置
                if (this.robot.details.angle_settings) {
                    try {
                        this.angleSettings = JSON.parse(this.robot.details.angle_settings)
                    } catch (error) {
                        console.error('解析角度设置失败:', error)
                        // 使用默认值
                        this.angleSettings = {
                            single_angle: 42,
                            double_left_angle: 42,
                            double_right_angle: 42,
                            off_angle: 90,
                        }
                    }
                }
            }).finally(() => {
                this.robotLoading = false;
            })
        },

        // 获取操作日志
        async fetchOperationLogs(isLoadMore = false) {
            if (isLoadMore) {
                this.loadingMore = true
            } else {
                this.logsLoading = true
                this.currentPage = 1
                this.operationRecords = []
                this.hasMoreLogs = true
            }
            
            try {
                const robotId = this.$route.params.id
                const params = {
                    robot_id: parseInt(robotId),
                    page: this.currentPage,
                    page_size: this.pageSize
                }
                
                const response = await getUserRobotLogs(params)
                if (response && response.data && response.data.list) {
                    // 转换后端日志数据为前端显示格式
                    const newRecords = response.data.list.map(log => ({
                        id: log.id,
                        time: log.created_at,
                        action: log.action,
                        result: log.result,
                        content: this.getActionText(log.action),
                        errorMessage: log.error_message,
                        isScheduledTask: log.is_scheduled_task,
                        operatorName: log.operator_name,
                    }))
                    
                    if (isLoadMore) {
                        // 追加到现有记录
                        this.operationRecords = [...this.operationRecords, ...newRecords]
                    } else {
                        // 替换现有记录
                        this.operationRecords = newRecords
                    }
                    
                    // 检查是否还有更多数据
                    this.hasMoreLogs = newRecords.length === this.pageSize
                } else {
                    if (!isLoadMore) {
                        this.operationRecords = []
                    }
                    this.hasMoreLogs = false
                }
            } finally {
                this.logsLoading = false
                this.loadingMore = false
            }
        },

        // 加载更多日志
        async loadMoreLogs() {
            if (this.loadingMore || !this.hasMoreLogs) {
                return
            }
            
            this.currentPage++
            await this.fetchOperationLogs(true)
        },

        // 添加滚动监听
        addScrollListener() {
            this.$nextTick(() => {
                const logsContainer = this.$el.querySelector('.logs-container')
                if (logsContainer) {
                    logsContainer.addEventListener('scroll', this.handleScroll)
                }
            })
        },

        // 移除滚动监听
        removeScrollListener() {
            const logsContainer = this.$el.querySelector('.logs-container')
            if (logsContainer) {
                logsContainer.removeEventListener('scroll', this.handleScroll)
            }
        },

        // 处理滚动事件
        handleScroll(event) {
            const container = event.target
            const scrollTop = container.scrollTop
            const scrollHeight = container.scrollHeight
            const clientHeight = container.clientHeight
            
            // 当滚动到底部附近时（距离底部50px内）触发加载更多
            if (scrollHeight - scrollTop - clientHeight < 50) {
                this.loadMoreLogs()
            }
        },

        // 控制机器人
        handleControlRobot(id, action) {
            this.isProcessing = true
            controlRobot(id, action).then(() => {
                this.robot.details.status = action
                this.fetchOperationLogs()
            }).finally(() => {
                setTimeout(() => {
                    this.isProcessing = false
                }, 500)
            })
        },

        // 按下左按钮
        pressLeft() {
            if (!this.robot.details.online) {
                Message.warning("机器人不在线")
                return
            }

            if (this.isProcessing) return

            this.handleControlRobot(this.robot.details.ID, 'double_left')
        },

        // 按下右按钮
        pressRight() {
            if (!this.robot.details.online) {
                Message.warning("机器人不在线")
                return
            }

            if (this.isProcessing) return

            this.handleControlRobot(this.robot.details.ID, 'double_right')
        },

        // 获取动作文本
        getActionText(action) {
            const actionMap = {
                'single_on': '单开型机器人开启',
                'off': '单开型机器人关闭',
                'double_left': '按下左按钮',
                'double_right': '按下右按钮',
                'start': '开始操作',
                'stop': '停止操作',
                'pause': '暂停操作',
                'resume': '继续操作'
            }
            return actionMap[action] || action
        },

        // 获取动作图标
        getActionIcon(action) {
            if (action.includes('left')) return 'el-icon-arrow-left'
            if (action.includes('right')) return 'el-icon-arrow-right'
            if (action === 'start' || action.includes('on')) return 'el-icon-video-play'
            if (action === 'stop' || action.includes('off')) return 'el-icon-video-pause'
            if (action === 'pause') return 'el-icon-video-pause'
            if (action === 'resume') return 'el-icon-video-play'
            if (action.includes('hold')) return 'el-icon-loading'
            return 'el-icon-operation'
        },

        // 格式化时间
        formatTime(time) {
            if (!time) return ''
            const date = new Date(time)
            const now = new Date()

            // 如果是今天，只显示时间
            if (date.toDateString() === now.toDateString()) {
                return date.toLocaleTimeString('zh-CN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            } else if (date.getFullYear() === now.getFullYear()) {
                // 如果是今年但不是今天，显示月日和时间
                return date.toLocaleString('zh-CN', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            } else {
                // 如果不是今年，显示年月日和时间
                return date.toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            }
        },

        // 跳转到分享页面
        goToSharePage() {
            this.$router.push(`/robot/share/${this.robot.details.ID}`)
        },

        // 取消分享
        cancelShare() {
            MessageBox.confirm(
                `确定要取消分享的机器人吗?\n取消后需要联系分享者才能再次获得分享权限。`, 
                '取消分享确认', 
                {
                    confirmButtonText: '确定删除',
                    cancelButtonText: '取消',
                    type: 'warning'
                }
            ).then(() => {
                deleteRobotShare(this.robot.details.ID).then(() => {
                    Message.success(`取消成功`)
                    this.goBack()
                })
            }).catch(() => {
                // 用户取消删除
            })
        },

        // 打开编辑弹窗
        openEditModal() {
            if (!this.robot || !this.robot.details) {
                this.$message.warning('机器人信息未加载')
                return
            }

            // 初始化表单数据
            this.editForm = {
                name: this.robot.details.name || '',
                status: this.robot.details.status || '',
                mode: this.robot.details.mode || 'tap',
                boot_execute: this.robot.details.boot_execute ?? false,
                silence_mode: this.robot.details.silence_mode || false,
                delay: this.robot.details.delay || 500,
                enable_bemfa: this.robot.details.enable_bemfa !== undefined ? this.robot.details.enable_bemfa : true,
            }

            this.editDialogVisible = true
            this.$nextTick(() => {
                if (this.$refs.editForm) {
                    this.$refs.editForm.clearValidate()
                }
            })
        },

        // 保存编辑
        async saveEdit() {
            try {
                await this.$refs.editForm.validate()
            } catch (error) {
                return
            }

            this.editLoading = true
            try {
                const data = {
                    name: this.editForm.name.trim(),
                    status: this.editForm.status,
                    mode: this.editForm.mode,
                    boot_execute: this.editForm.boot_execute,
                    silence_mode: this.editForm.silence_mode,
                    delay: Number(this.editForm.delay),
                    enable_bemfa: this.editForm.enable_bemfa,
                }

                await updateRobot(this.robot.details.ID, data)
                this.$message.success('保存成功')
                this.editDialogVisible = false
                this.fetchRobot()
            } finally {
                this.editLoading = false
            }
        },

        // 打开角度编辑弹窗
        openAngleModal() {
            if (!this.robot || !this.robot.details) {
                this.$message.warning('机器人信息未加载')
                return
            }

            // 初始化表单数据
            this.angleForm = {
                single_angle: this.angleSettings.single_angle ?? 42,
                double_left_angle: this.angleSettings.double_left_angle ?? 42,
                double_right_angle: this.angleSettings.double_right_angle ?? 42,
                off_angle: this.angleSettings.off_angle ?? 90,
            }

            this.angleDialogVisible = true
            this.$nextTick(() => {
                if (this.$refs.angleForm) {
                    this.$refs.angleForm.clearValidate()
                }
            })
        },

        // 重置角度设置
        async resetAngleSettings() {
            const response = await getDefaultAngleSettings()
            const defaultSettings = response.data

            this.angleForm = {
                single_angle: defaultSettings.single_angle ?? 42,
                double_left_angle: defaultSettings.double_left_angle ?? 42,
                double_right_angle: defaultSettings.double_right_angle ?? 42,
                off_angle: defaultSettings.off_angle ?? 90,
            }

            this.$message.success('已重置为默认值')
        },

        // 保存角度设置
        async saveAngleSettings() {
            try {
                await this.$refs.angleForm.validate()
            } catch (error) {
                return
            }

            this.angleLoading = true
            try {
                const data = {
                    angle_settings: {
                        single_angle: Number(this.angleForm.single_angle),
                        double_left_angle: Number(this.angleForm.double_left_angle),
                        double_right_angle: Number(this.angleForm.double_right_angle),
                        off_angle: Number(this.angleForm.off_angle),
                    },
                }

                await updateRobotAngles(this.robot.details.ID, data)
                this.$message.success('保存成功')
                this.angleDialogVisible = false
                this.fetchRobot()
            } finally {
                this.angleLoading = false
            }
        },

        // 获取定时任务列表
        async fetchScheduledTasks() {
            this.tasksLoading = true
            try {
                const params = {
                    robot_id: parseInt(this.$route.params.id),
                }
                const response = await getScheduledTasks(params)
                if (response && response.data && response.data.tasks) {
                    this.scheduledTasks = response.data.tasks
                } else {
                    this.scheduledTasks = []
                }
            } finally {
                this.tasksLoading = false
            }
        },

        // 打开创建任务弹窗
        openCreateTaskModal() {
            if (!this.robot || !this.robot.details) {
                this.$message.warning('机器人信息未加载')
                return
            }

            // 检查是否可以添加更多任务
            const timedTasks = this.scheduledTasks.filter(
                t => this.isTimedScheduleType(t.type)
            )
            const intervalTasks = this.scheduledTasks.filter(
                t => t.type === 'interval'
            )

            if (timedTasks.length >= this.maxFixedTimeTasks && intervalTasks.length >= 1) {
                this.$message.warning('已达到任务数量上限')
                return
            }

            // 设置为创建模式
            this.isEditingTask = false
            this.editingTaskId = null

            // 初始化表单数据
            this.scheduleForm = {
                name: '',
                type: 'daily',
                action: this.actionOptions.length > 0 ? this.actionOptions[0].value : '',
                schedule_time: '00:00:00',
                hour: 0,
                minute: 0,
                second: 0,
                weekdays: [],
                interval_seconds: 5,
            }
            this.scheduleDialogVisible = true
            this.$nextTick(() => {
                if (this.$refs.scheduleForm) {
                    this.$refs.scheduleForm.clearValidate()
                }
            })
        },

        // 打开编辑任务弹窗
        openEditTaskModal(task) {
            if (!this.canEditSettings) {
                return
            }
            
            if (!this.robot || !this.robot.details) {
                this.$message.warning('机器人信息未加载')
                return
            }

            // 设置为编辑模式
            this.isEditingTask = true
            this.editingTaskId = task.ID

            // 填充表单数据
            const weekdays = task.weekdays ? task.weekdays.split(',').map(Number) : []
            const hour = String(task.hour || 0).padStart(2, '0')
            const minute = String(task.minute || 0).padStart(2, '0')
            const second = String(task.second || 0).padStart(2, '0')

            this.scheduleForm = {
                name: task.name || '',
                type: task.type || 'daily',
                action: task.action || '',
                schedule_time: `${hour}:${minute}:${second}`,
                hour: task.hour || 0,
                minute: task.minute || 0,
                second: task.second || 0,
                weekdays: weekdays,
                interval_seconds: task.interval_seconds || 5,
            }

            this.scheduleDialogVisible = true
            this.$nextTick(() => {
                if (this.$refs.scheduleForm) {
                    this.$refs.scheduleForm.clearValidate()
                }
            })
        },

        // 任务类型改变
        onTaskTypeChange(value) {
            // 检查是否可以添加该类型的任务（仅在创建模式下）
            if (!this.isEditingTask) {
                const intervalTasks = this.scheduledTasks.filter(t => t.type === 'interval')
                const timedTasks = this.scheduledTasks.filter(t => this.isTimedScheduleType(t.type))
                
                if (value === 'interval' && intervalTasks.length >= 1) {
                    this.$message.warning('循环执行任务只能创建1个')
                } else if (this.isTimedScheduleType(value) && timedTasks.length >= this.maxFixedTimeTasks) {
                    this.$message.warning(`固定时间任务最多创建${this.maxFixedTimeTasks}个`)
                }
            }
        },

        // 保存定时任务
        async saveScheduleTask() {
            try {
                await this.$refs.scheduleForm.validate()
            } catch (error) {
                return
            }

            // 只在创建模式下检查任务数量限制
            if (!this.isEditingTask) {
                const intervalTasks = this.scheduledTasks.filter(t => t.type === 'interval')
                const timedTasks = this.scheduledTasks.filter(t => this.isTimedScheduleType(t.type))
                
                if (this.scheduleForm.type === 'interval' && intervalTasks.length >= 1) {
                    this.$message.warning('循环执行任务只能创建1个')
                    return
                } else if (this.isTimedScheduleType(this.scheduleForm.type) && timedTasks.length >= this.maxFixedTimeTasks) {
                    this.$message.warning(`固定时间任务最多创建${this.maxFixedTimeTasks}个`)
                    return
                }
            }

            this.scheduleLoading = true
            try {
                const data = {
                    robot_id: parseInt(this.$route.params.id),
                    name: this.scheduleForm.name.trim(),
                    type: this.scheduleForm.type,
                    action: this.scheduleForm.action,
                }

                // 解析时间选择器的值
                if (this.isTimedScheduleType(this.scheduleForm.type)) {
                    if (this.scheduleForm.schedule_time) {
                        const timeParts = this.scheduleForm.schedule_time.split(':')
                        data.hour = parseInt(timeParts[0]) || 0
                        data.minute = parseInt(timeParts[1]) || 0
                        data.second = parseInt(timeParts[2]) || 0
                    } else {
                        data.hour = 0
                        data.minute = 0
                        data.second = 0
                    }
                }

                // 根据类型添加配置
                if (this.scheduleForm.type === 'weekly') {
                    data.weekdays = this.scheduleForm.weekdays.sort((a, b) => a - b)
                } else if (this.scheduleForm.type === 'interval') {
                    data.interval_seconds = Number(this.scheduleForm.interval_seconds)
                }

                // 根据模式调用不同的API
                if (this.isEditingTask) {
                    await updateScheduledTask(this.editingTaskId, data)
                    this.$message.success('保存成功')
                } else {
                    await createScheduledTask(data)
                    this.$message.success('创建成功')
                }

                this.scheduleDialogVisible = false
                this.fetchScheduledTasks()
            } finally {
                this.scheduleLoading = false
            }
        },

        // 切换任务启用状态
        async toggleTaskEnabled(task) {
            await toggleScheduledTask(task.ID, !task.enabled)
            this.$message.success(task.enabled ? '已禁用' : '已启用')
            this.fetchScheduledTasks()
        },

        // 删除任务
        async deleteTask(task) {
            try {
                await this.$confirm(`确定要删除任务"${task.name}"吗？`, '确认删除', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
            })
            
            await deleteScheduledTask(task.ID)
            this.$message.success('删除成功')
            this.fetchScheduledTasks()
        } catch (error) {
            if (error === 'cancel') {
                return
            }
        }
        },

        isTimedScheduleType(type) {
            return ['daily', 'weekly', 'workday', 'holiday'].includes(type)
        },

        // 获取任务类型显示文本
        getTaskTypeLabel(type) {
            const option = this.taskTypeOptions.find(opt => opt.value === type)
            return option ? option.label : type
        },

        // 获取动作显示文本
        getActionLabel(action) {
            const actionMap = {
                single_on: '开启',
                single_switch: '切换',
                double_left: '左开',
                double_right: '右开',
                double_switch: '左右切换',
                off: '关闭',
            }
            return actionMap[action] || action
        },

        // 格式化任务描述
        formatTaskDescription(task) {
            if (task.type === 'daily') {
                const hour = String(task.hour).padStart(2, '0')
                const minute = String(task.minute).padStart(2, '0')
                const second = String(task.second).padStart(2, '0')
                return `每天 ${hour}:${minute}:${second}`
            } else if (task.type === 'weekly') {
                const weekdays = task.weekdays ? task.weekdays.split(',').map(Number) : []
                const weekdayLabels = weekdays.map(wd => {
                    const option = this.weekdayOptions.find(opt => opt.value === wd)
                    return option ? option.label : ''
                }).join('、')
                const hour = String(task.hour).padStart(2, '0')
                const minute = String(task.minute).padStart(2, '0')
                const second = String(task.second).padStart(2, '0')
                return `${weekdayLabels} ${hour}:${minute}:${second}`
            } else if (task.type === 'workday' || task.type === 'holiday') {
                const hour = String(task.hour).padStart(2, '0')
                const minute = String(task.minute).padStart(2, '0')
                const second = String(task.second).padStart(2, '0')
                return `${this.getTaskTypeLabel(task.type)} ${hour}:${minute}:${second}`
            } else if (task.type === 'interval') {
                return `每隔 ${task.interval_seconds} 秒`
            }
            return ''
        },

        // 返回上一页
        goBack() {
            this.$router.push('/')
        },

        // 点击控制面板标题
        handleTitleClick() {
            if (!this.isAdmin) return

            this.titleClickCount++
            
            // 清除之前的定时器
            if (this.titleClickTimer) {
                clearTimeout(this.titleClickTimer)
            }

            // 如果点击5次，显示管理员调试卡片
            if (this.titleClickCount >= 5) {
                this.showAdminDebug = true
                this.titleClickCount = 0
                this.$message.success('管理员调试模式已开启')
                return
            }

            // 设置定时器，2秒后重置计数
            this.titleClickTimer = setTimeout(() => {
                this.titleClickCount = 0
            }, 2000)
        },

        // 恢复出厂设置
        async handleRestoreFactory(type) {
            try {
                await this.$confirm('确定要将此机器人恢复出厂设置吗？此操作将清除所有自定义配置！', '确认恢复', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                })

                if (type === 'pro') {
                  await restoreRobot(this.robot.details.ID)
                } else if (type === 'bemfa') {
                  await restoreBemfaRobot()
                } else {
                  this.$message.error('找不到此重置类型')
                  return
                }
                this.$message.success('恢复出厂设置成功')
                
                // 刷新机器人信息
                this.fetchRobot()
            } catch (error) {
                if (error === 'cancel') {
                    return
                }
            }
        }
    }
}
</script>

<style scoped>
.robot-control {
    min-height: 100vh;
    background-color: #f5f7fa;
    padding: 20px;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eaeaea;
}

.header h1 {
    margin: 0;
    color: #303133;
}

.back-btn {
    font-size: 16px;
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
}

.loading-text {
    text-align: center;
    margin-top: 15px;
    color: #606266;
}

.main-content {
    max-width: 900px;
    margin: 0 auto;
}

.robot-info-card {
    margin-bottom: 30px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 15px;
}

.card-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
}

.robot-details {
    margin-top: 15px;
}

.detail-row {
    display: flex;
    gap: 40px;
}

.detail-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.share-info-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #eaeaea;
}

.detail-item {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
}

.detail-label {
    color: #606266;
    width: 100px;
}

.detail-value {
    color: #303133;
    font-weight: 500;
}

.detail-value-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
}

.status-value-tag {
    font-size: 14px;
}

.control-panel {
    background-color: #ffffff;
    border-radius: 8px;
    padding: 25px;
    margin-bottom: 30px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.control-panel h3 {
    margin-top: 0;
    margin-bottom: 20px;
    color: #303133;
    font-size: 18px;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 10px;
}

/* 单开机器人样式 */
.single-control-section {
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* 单开控制按钮 */
.single-toggle-btn {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #ffffff;
    transition: all 0.3s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    -webkit-tap-highlight-color: transparent;
}

.single-btn-offline-text {
    font-size: 2.5rem;
}

.single-toggle-btn:hover:not(.disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.single-toggle-btn.active {
    background-color: #409eff;
    border-color: #409eff;
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.single-toggle-btn.active i {
    color: #ffffff;
}

.single-toggle-btn.disabled {
    background-color: #f5f7fa;
    border-color: #dcdfe6;
    cursor: not-allowed;
    opacity: 0.6;
}

.single-toggle-btn i {
    font-size: 22px;
    color: #409eff;
    transition: all 0.3s ease;
}

/* 双开型机器人按钮样式 */
.double-controls-new {
    display: flex;
    gap: 15px;
    align-items: center;
}

.double-btn {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #ffffff;
    transition: all 0.3s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    -webkit-tap-highlight-color: transparent;
}

.double-btn:hover:not(.disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.double-btn.active {
    background-color: #409eff;
    border-color: #409eff;
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.double-btn.active i {
    color: #ffffff;
}

.double-btn i {
    font-size: 22px;
    color: #409eff;
    transition: all 0.3s ease;
}

.double-btn.disabled {
    background-color: #f5f7fa;
    border-color: #dcdfe6;
    cursor: not-allowed;
    opacity: 0.6;
}

/* 控制按钮区域 */
.double-control-section {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.offline-notice {
    margin-top: 20px;
}

.operation-history {
    background-color: #ffffff;
    border-radius: 8px;
    padding: 25px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.operation-history h3 {
    margin-top: 0;
    margin-bottom: 20px;
    color: #303133;
    font-size: 18px;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 10px;
}

.record-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 8px;
}

.record-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    flex: 1;
}

.record-info i {
    color: #409eff;
    font-size: 16px;
}

.record-text {
    flex: 1;
    font-size: 14px;
    color: #303133;
}

.operator-info {
    flex-shrink: 0;
}

.result-tag {
    margin-left: auto;
}

.error-message {
    margin-top: 10px;
}

/* 帮助图标样式 */
.help-icon {
    color: #909399;
    font-size: 16px;
    cursor: help;
    margin-left: 5px;
}

.help-icon:hover {
    color: #409eff;
}

.schedule-action-help-icon {
    cursor: pointer;
}

/* 分享设置样式 */
.share-settings-section {
    margin-bottom: 30px;
}

.share-hint {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 15px;
    background-color: #f0f9ff;
    border-radius: 8px;
    border: 1px solid #b3d8ff;
}

.share-hint i {
    font-size: 24px;
    color: #409eff;
}

.share-hint span {
    color: #409eff;
    font-size: 14px;
}

/* 定时任务管理样式 */
.schedule-tasks-section {
    margin-bottom: 30px;
}

.tasks-loading {
    min-height: 100px;
}

.tasks-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.task-item {
    border-radius: 8px;
    padding: 15px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.task-item:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.task-item-disabled {
    cursor: default;
    opacity: 0.8;
}

.task-item-disabled:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.task-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eaeaea;
}

.task-name {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    flex: 1;
}

.task-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 10px;
}

.task-detail-row {
    display: flex;
    align-items: center;
    font-size: 14px;
}

.task-label {
    color: #909399;
    min-width: 80px;
}

.task-value {
    color: #303133;
    font-weight: 500;
    flex: 1;
}

.task-actions {
    display: flex;
    gap: 10px;
    padding-top: 10px;
    border-top: 1px solid #eaeaea;
}

.task-actions .el-button {
    flex: 1;
}

.delete-btn {
    color: #f56c6c;
}

/* 对话框样式 */
.angle-dialog-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 48px;
    color: #303133;
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
    margin-top: 5px;
}

.el-form-item__content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.el-form-item__content .el-slider {
    flex: 1;
    margin-left: 10px;
}

.logs-container {
    max-height: 400px;
    overflow-y: auto;
    padding-right: 10px;
    padding-top: 20px;
}

.logs-container::-webkit-scrollbar {
    width: 6px;
}

.logs-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.logs-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.logs-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.logs-loading {
    min-height: 200px;
}

.loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #606266;
    font-size: 14px;
}

.loading-more span {
    margin-left: 8px;
}

.no-more-data {
    text-align: center;
    padding: 20px;
    color: #909399;
    font-size: 14px;
    border-top: 1px solid #f0f0f0;
    margin-top: 10px;
}


.error-container {
    text-align: center;
    padding: 60px 20px;
}

.retry-btn {
    margin-top: 20px;
}

/* 管理员调试卡片样式 */
.admin-debug-card {
    border: 2px solid #f56c6c;
    background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);
}

.admin-debug-card .card-header h3 {
    color: #f56c6c;
}

.debug-buttons {
    display: flex;
    gap: 10px;
}

.debug-actions {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .robot-control {
        padding: 10px;
    }

    .header {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }

    .back-btn {
        align-self: flex-start;
    }

    .single-arm-system {
        width: 240px;
        height: 130px;
    }

    .single-arm {
        width: 120px;
    }

    .single-base {
        width: 50px;
        height: 50px;
        left: 55px;
    }

    .single-target-button {
        width: 40px;
        height: 40px;
        right: 15px;
    }

    .dual-arm-system {
        width: 300px;
        height: 180px;
    }

    .seesaw-bar {
        width: 240px;
    }

    .central-base {
        width: 60px;
        height: 45px;
    }

    .central-base::before {
        width: 45px;
        height: 30px;
    }

    .target-buttons-container {
        padding: 0 20px;
    }

    .target-button {
        width: 35px;
        height: 35px;
    }

    .target-button::after {
        width: 20px;
        height: 20px;
    }

    .detail-item {
        flex-direction: row;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
    }

    .detail-label {
        width: auto;
        min-width: 80px;
        flex-shrink: 0;
    }

    .detail-value {
        flex: 1;
        min-width: 0;
    }
}
</style>
